import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, ChevronRight, X, AlertTriangle, Users, PawPrint, ToggleLeft, ToggleRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { getCustomers, getCustomerStats } from '../services/api';
import Loader from '../components/Loader';
import BottomNav from '../components/BottomNav';
import PetAvatar from '../components/PetAvatar';
import PetIcon from '../components/PetIcon';

const formatCurrency = (amount) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(Math.abs(amount));

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debtorsOnly, setDebtorsOnly] = useState(false);
  const [stats, setStats] = useState({ customer_count: 0, pet_count: 0 });

  useEffect(() => {
    getCustomerStats().then(res => setStats(res.data)).catch(() => {});
  }, []);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCustomers(search || undefined, debtorsOnly || undefined);
      setCustomers(res.data);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  }, [search, debtorsOnly]);

  useEffect(() => {
    const timer = setTimeout(() => { loadCustomers(); }, search ? 300 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCustomers, search]);

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-24 lg:pb-4" data-testid="customers-page">
      <header className="bg-white px-4 pt-12 lg:pt-4 pb-4 shadow-sm sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">{`Müşteriler`}</h1>
            <span className="flex items-center gap-1 bg-[#6b6de3]/10 text-[#6b6de3] rounded-full px-2.5 py-0.5 text-xs font-bold" data-testid="customer-count-badge">
              <Users size={13} /> {stats.customer_count}
            </span>
          </div>
          <Button
            onClick={() => navigate('/quick-add')}
            className="bg-[#6b6de3] hover:bg-[#5658c4] rounded-full h-9 px-4 text-sm"
            data-testid="add-customer-btn"
          >
            <Plus size={18} className="mr-1" />
            Ekle
          </Button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={18} />
          <Input
            type="search"
            placeholder={`Müşteri Ara (İsim, telefon veya TCKN)`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-10 h-11 text-sm bg-[#F2F2F7] border-0 rounded-xl"
            data-testid="customers-search-input"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8E8E93]">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Debtors Toggle */}
        <div className="flex items-center justify-between mt-3" data-testid="debtors-filter">
          <span className="text-sm text-[#8E8E93] flex items-center gap-1.5">
            <AlertTriangle size={14} className="text-[#FF3B30]" />
            {`Borçlu müşteriler`}
          </span>
          <button onClick={() => setDebtorsOnly(!debtorsOnly)} data-testid="debtors-toggle">
            {debtorsOnly
              ? <ToggleRight size={36} className="text-[#FF3B30]" />
              : <ToggleLeft size={36} className="text-[#C7C7CC]" />}
          </button>
        </div>
      </header>

      <main className="p-4">
        {loading ? (
          <div className="py-12 flex justify-center"><Loader /></div>
        ) : customers.length > 0 ? (
          <div className="ios-card divide-y divide-gray-100 p-0 overflow-hidden">
            {customers.map((customer) => (
              <button
                key={customer.id}
                onClick={() => navigate(`/customer/${customer.id}`)}
                className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                data-testid={`customer-${customer.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 text-sm">{customer.name}</p>
                    <span className="text-xs text-[#8E8E93]">{customer.phone}</span>
                  </div>
                  {customer.pets?.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {customer.pets.map((pet) => (
                        <span key={pet.id} className="flex items-center gap-1 text-xs text-[#8E8E93]">
                          <PetAvatar species={pet.species} gender={pet.gender} size={11} />
                          {pet.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  {customer.balance !== 0 && (
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      customer.balance < 0
                        ? 'bg-red-50 text-[#FF3B30]'
                        : 'bg-green-50 text-[#34C759]'
                    }`} data-testid={`balance-${customer.id}`}>
                      {customer.balance < 0 ? `-${formatCurrency(customer.balance)}` : `+${formatCurrency(customer.balance)}`}
                    </span>
                  )}
                  <ChevronRight size={20} className="text-[#C7C7CC]" />
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="ios-card text-center py-12">
            <PawPrint size={48} className="text-[#C7C7CC] mx-auto mb-3" />
            <p className="text-[#8E8E93]">{search ? `Sonuç bulunamadı` : debtorsOnly ? `Borçlu müşteri yok` : `Henüz müşteri yok`}</p>
            {!search && !debtorsOnly && (
              <Button onClick={() => navigate('/quick-add')} className="mt-4 bg-[#6b6de3] hover:bg-[#5658c4] rounded-full">
                <Plus size={18} className="mr-1" /> {`İlk Müşteriyi Ekle`}
              </Button>
            )}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default Customers;
